import { CloseSmall } from '@elliemae/ds-icons';
import { DSToolbar, ToolbarGroup, ToolbarItem } from '@elliemae/ds-toolbar';
import { DSButtonV3 } from '@elliemae/ds-button-v2';
import ssf from '@elliemae/em-ssf-guest';
import {
  useLoanDataFieldSelectorHook,
  useLookupOptions,
  useUserData,
} from '@frontend/pricing-search';
import {
  identifyLogRocketUser,
  trackLogRocketEvent,
} from '@frontend/logrocket';
import { bool, func, string } from 'prop-types';
import { useCallback, useEffect, useRef } from 'react';
import { getHelpUrl } from '@frontend/utils';
import { getAppConfigValue } from '@elliemae/pui-app-sdk';
import { shouldIdentifyOwnSession } from '../../../../../common/services/launchContext';
import { LockDeskStatus } from '../LockDeskStatus';
import { HELP_TEXT, PRICING_TEXT } from '../utils/constants';
import { StyledDebugInfo, StyledSlash } from '../utils/styled';
import {
  PATH_LOCK_SUMMARY,
  PATH_SEARCH_FOR_PRODUCTS,
  PATH_SEARCH_RESULTS,
} from '../../../../../route/constants';
import Session, { ORIGIN_SOURCE } from '../../../../../common/services/Session';

export const HeaderToolbar = ({
  isSmartClient,
  handleOpenModal,
  loanId,
}: Partial<{
  isSmartClient: boolean;
  handleOpenModal: any;
  loanId: string;
}>) => {
  const { firstName, lastName } = useUserData();
  const isSecondary = Session.get(ORIGIN_SOURCE) === 'secondarylock';
  const pathsToShowSmartClientLauncher = [
    PATH_SEARCH_FOR_PRODUCTS,
    PATH_SEARCH_RESULTS,
    PATH_LOCK_SUMMARY,
  ];
  const eppsUserName = useLoanDataFieldSelectorHook('eppsUserName') as any;
  const officersOptions = useLookupOptions('transformedOfficeUsers');
  const loanOfficer =
    eppsUserName && officersOptions
      ? officersOptions.find((option) => option.login === eppsUserName.value)
      : null;
  const displayName = loanOfficer?.label ?? `${firstName} ${lastName}`;
  const helpUrl = getHelpUrl(getAppConfigValue('laneTag') as string);

  const isVisibleLockDesk = false; // APPLY_EPPS_LOCKDESK_SETTINGS && isBuySide && isValidChannel;

  const hasIdentified = useRef(false);
  useEffect(() => {
    if (hasIdentified.current || !displayName || !loanId) {
      return;
    }
    // Only SmartClient and standalone record as their own identified session.
    // ENCW and TPO roll up into the parent host's session replay.
    if (shouldIdentifyOwnSession()) {
      identifyLogRocketUser({
        name: `${displayName} | Pricing ID ${loanId}`,
      });
    }
    hasIdentified.current = true;
  }, [displayName, loanId]);

  useEffect(() => {
    if (loanId) {
      trackLogRocketEvent('pricing-context', { pricingId: loanId });
    }
  }, [loanId]);

  const viewLoanForms = useCallback(async () => {
    const applicationObject = await ssf.getObject('application');
    await applicationObject.performAction('launchLoanEditor');
  }, []);

  if (isSmartClient) {
    return (
      <DSToolbar size="compact" withDepth={false} autoFocusOnMount={false}>
        <ToolbarGroup>
          {isVisibleLockDesk && <LockDeskStatus />}
          <StyledDebugInfo data-testid="HeaderToolbar-all-components">
            <a
              href={helpUrl}
              target="_blank"
              rel="noreferrer"
              style={{ textDecoration: 'none' }}
            >
              {HELP_TEXT}
            </a>
          </StyledDebugInfo>
          {pathsToShowSmartClientLauncher.includes(
            window?.location?.pathname,
          ) &&
            !isSecondary && (
              <DSButtonV3 buttonType="outline" onClick={() => viewLoanForms()}>
                View Loan Forms
              </DSButtonV3>
            )}
        </ToolbarGroup>
      </DSToolbar>
    );
  }
  return (
    <DSToolbar size="compact" withDepth={false} autoFocusOnMount={false}>
      <ToolbarGroup>
        {isVisibleLockDesk && (
          <>
            <LockDeskStatus />
            <div style={{ marginLeft: '16px' }} />
            <StyledSlash />
          </>
        )}
        <StyledDebugInfo data-testid="HeaderToolbar-all-components">
          <a
            href={helpUrl}
            target="_blank"
            rel="noreferrer"
            style={{ textDecoration: 'none' }}
          >
            {HELP_TEXT}
          </a>
        </StyledDebugInfo>
        {firstName && lastName && (
          <>
            <StyledDebugInfo data-testid="HeaderToolbar-all-components">
              {displayName}
            </StyledDebugInfo>
            <StyledSlash />
          </>
        )}
        {loanId && (
          <>
            <StyledDebugInfo data-testid="HeaderToolbar-all-components">
              {PRICING_TEXT + loanId}
            </StyledDebugInfo>
            <StyledSlash />
          </>
        )}
        <ToolbarItem
          icon={<CloseSmall />}
          onClick={handleOpenModal}
          style={{ marginLeft: '7px', paddingTop: '6px' }}
        />
      </ToolbarGroup>
    </DSToolbar>
  );
};

HeaderToolbar.defaultProps = {
  loanId: '',
  isSmartClient: false,
  handleOpenModal: () => {},
};

HeaderToolbar.propTypes = {
  loanId: string,
  isSmartClient: bool,
  handleOpenModal: func,
};
